import type { ComponentProps } from 'react'
import { useCallback, useMemo, useState } from 'react'

import { TextField } from '@/components/ui/text-field.tsx'
import type { ManagedField } from '@/modules/message/components/editor/fields/managed-field'
import { PlaceholderSelector } from '@/modules/message/components/editor/fields/placeholder-selector'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context.tsx'
import type { PlaceholderVariant } from '@/modules/message/components/placeholder/variants'
import {
  detectVariant,
  variants,
} from '@/modules/message/components/placeholder/variants'
import { classNames } from '@/utils/helpers'

export type MessageFallbackSelectorProps = ComponentProps<'div'> & {
  inputClassName?: string
  fallback?: ManagedField<string>
  fallbackVariable?: ManagedField<string>
  enableFallbackVariable?: ManagedField<boolean>
}

export const MessageFallbackSelector = (
  props: MessageFallbackSelectorProps,
) => {
  const {
    className,
    inputClassName,
    fallback,
    fallbackVariable,
    enableFallbackVariable,
    ...rest
  } = props

  const isUsingVariable = enableFallbackVariable?.value === true
  const fallbackDisplayValue = fallback?.value ?? ''

  const { hasCustomNamePlaceholders, hasPlaceholders } = useMessageEditor()

  return (
    <div
      {...rest}
      className={classNames(
        'flex flex-col gap-1.5',
        className,
        !hasPlaceholders && 'cursor-not-allowed',
      )}
    >
      <label className="text-sm font-medium text-text-default">
        Fallback Name
      </label>
      <div className="relative flex flex-grow items-center">
        <TextField
          {...fallback}
          value={fallbackDisplayValue}
          placeholder="Enter Name"
          withEmojiPicker
          testId="fallback-name-input"
          disabled={!hasPlaceholders}
          className={classNames('w-full', inputClassName)}
        />
      </div>
      {fallback?.error && (
        <span className="text-xs font-medium text-danger-default">
          {fallback?.error.message}
        </span>
      )}
      {hasCustomNamePlaceholders && (
        <div className="text-sm text-title">
          <input
            checked={enableFallbackVariable?.value}
            onChange={enableFallbackVariable?.onChange}
            type="checkbox"
            className="mr-2 accent-primary-500"
          />
          Replace Custom Name with{' '}
          <FallbackVariableSelector
            fallbackVariable={fallbackVariable}
            isUsingVariable={isUsingVariable}
          />{' '}
          if you haven’t set a Custom Name for the fan, instead of the Fallback
          Name
        </div>
      )}
    </div>
  )
}

interface FallbackVariableSelectorProps {
  fallbackVariable?: ManagedField<string>
  isUsingVariable: boolean
}

const FallbackVariableSelector = (props: FallbackVariableSelectorProps) => {
  const { fallbackVariable, isUsingVariable } = props
  const [onlyFirst, setOnlyFirst] = useState(false)

  const variantGroup = onlyFirst
    ? variants.firstFanNameOptions
    : variants.fanNameOptions

  const name = useMemo(() => {
    const placeholder = detectVariant(
      fallbackVariable?.value?.replaceAll('%', '') ?? '',
    )

    return placeholder?.name ?? 'Select Fallback'
  }, [fallbackVariable?.value])

  const handleClick = useCallback(
    (variant: PlaceholderVariant) =>
      fallbackVariable?.onChange?.({ target: { value: `%${variant.id}%` } }),
    [fallbackVariable],
  )

  const handleToggle = useCallback(() => {
    setOnlyFirst((state) => !state)
  }, [])

  return (
    <PlaceholderSelector
      className={classNames('mx-2 inline-flex', {
        'pointer-events-none opacity-50': !isUsingVariable,
      })}
      name={name}
      variants={variantGroup}
      onClick={handleClick}
      onToggle={handleToggle}
      toggleValue={onlyFirst}
      toggleName="Only First Fan Name"
      testId="fallback-variable-selector"
    />
  )
}
